import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import {
  ClientDetailHeader,
  ClientOverviewTab,
  ClientProjectsTab,
  ClientInvoicesTab,
  ClientQuotesTab,
  ClientDocumentsTab,
  ClientHistoryTab,
  ClientEmailsTab,
  AssignAdminModal,
  SendEmailModal,
} from '@/components/admin/clients'
import { ShareClientModal } from '@/components/admin/ShareClientModal'
import { useClientDetail } from '@/hooks/useClientDetail'
import { useClientDocuments } from '@/hooks/useClients'
import {
  Pencil,
  Mail,
  FolderPlus,
  Receipt,
  FileText,
  Users,
  Share2,
  ArrowLeft,
  Landmark,
} from 'lucide-react'
import { useContacts } from '@/hooks/useContacts'
import toast from 'react-hot-toast'

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    client,
    projects,
    factures,
    devis,
    assignedAdmins,
    metrics,
    loading: isLoading,
    error,
  } = useClientDetail(id)
  const { data: documents = [] } = useClientDocuments(id)
  const { syncToQonto, isSyncingToQonto } = useContacts()
  const [activeTab, setActiveTab] = useState('overview')
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brown-900 mb-2">
          Client non trouvé
        </h2>
        <p className="text-brown-600 mb-4">
          {error || "Le client demandé n'existe pas ou a été supprimé."}
        </p>
        <Link to="/clients">
          <Button variant="outline">Retour à la liste</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Clients', href: '/clients' },
          { label: client.full_name },
        ]}
      />

      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft className="w-4 h-4" />}
        onClick={() => navigate('/clients')}
      >
        Retour
      </Button>

      {/* Header */}
      <ClientDetailHeader client={client} />

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Link to={`/clients/${client.id}/edit`}>
          <Button variant="outline" leftIcon={<Pencil className="w-4 h-4" />}>
            Modifier
          </Button>
        </Link>
        <Button
          variant="outline"
          leftIcon={<Mail className="w-4 h-4" />}
          onClick={() => setShowEmailModal(true)}
        >
          Email
        </Button>
        <Link to={`/projects/new?client=${client.id}`}>
          <Button variant="outline" leftIcon={<FolderPlus className="w-4 h-4" />}>
            Projet
          </Button>
        </Link>
        <Link to={`/factures/new?client=${client.id}`}>
          <Button variant="outline" leftIcon={<Receipt className="w-4 h-4" />}>
            Facture
          </Button>
        </Link>
        <Link to={`/devis/new?client=${client.id}`}>
          <Button variant="outline" leftIcon={<FileText className="w-4 h-4" />}>
            Devis
          </Button>
        </Link>
        <Button
          variant="outline"
          leftIcon={<Users className="w-4 h-4" />}
          onClick={() => setShowAssignModal(true)}
        >
          Attribuer
        </Button>
        <Button
          variant="outline"
          leftIcon={<Landmark className="w-4 h-4" />}
          onClick={async () => {
            try {
              await syncToQonto(client.id)
              toast.success('Client synchronise avec Qonto')
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Erreur sync Qonto')
            }
          }}
          disabled={isSyncingToQonto}
        >
          {isSyncingToQonto ? 'Sync...' : 'Sync Qonto'}
        </Button>
        <Button
          variant="outline"
          leftIcon={<Share2 className="w-4 h-4" />}
          onClick={() => setShowShareModal(true)}
        >
          Partager
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Aperçu</TabsTrigger>
          <TabsTrigger value="projects">
            Projets ({projects.length})
          </TabsTrigger>
          <TabsTrigger value="invoices">
            Factures ({factures.length})
          </TabsTrigger>
          <TabsTrigger value="quotes">
            Devis ({devis.length})
          </TabsTrigger>
          <TabsTrigger value="emails">
            Emails
          </TabsTrigger>
          <TabsTrigger value="documents">
            Documents (0)
          </TabsTrigger>
          <TabsTrigger value="history">Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <ClientOverviewTab
            client={client}
            projects={projects}
            factures={factures}
            devis={devis}
            assignedAdmins={assignedAdmins}
            metrics={metrics}
          />
        </TabsContent>
        <TabsContent value="projects">
          <ClientProjectsTab projects={projects} clientId={client.id} />
        </TabsContent>
        <TabsContent value="invoices">
          <ClientInvoicesTab invoices={factures} clientId={client.id} />
        </TabsContent>
        <TabsContent value="quotes">
          <ClientQuotesTab quotes={devis} clientId={client.id} />
        </TabsContent>
        <TabsContent value="emails">
          <ClientEmailsTab
            clientId={client.id}
            onSendEmail={() => setShowEmailModal(true)}
          />
        </TabsContent>
        <TabsContent value="documents">
          <ClientDocumentsTab documents={documents} clientId={client.id} />
        </TabsContent>
        <TabsContent value="history">
          <ClientHistoryTab logs={[]} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <AssignAdminModal
        client={client}
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
      />
      <ShareClientModal
        client={client}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
      <SendEmailModal
        client={client}
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
      />
    </div>
  )
}
